import { useState, useEffect } from 'react';
import { isBrowser } from 'libs/utils/dom';

export type UseScriptProps = {
  src: HTMLScriptElement['src'] | null;
  checkForExisting?: boolean;
  [key: string]: Any;
  runScript?: boolean;
  element: HTMLElement;
};

type ErrorState = ErrorEvent | null;

const useScript = ({
  src,
  checkForExisting = false,
  runScript = true,
  element = document.head,
  ...attributes
}: UseScriptProps): [boolean, ErrorState] => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<ErrorState>(null);

  useEffect(() => {
    if (!isBrowser || !src || !loading || error || !runScript) return;
    if (checkForExisting) {
      const existing = document.querySelectorAll(`script[src="${src}"]`);
      if (existing && existing.length > 0) {
        setLoading(false);
        return;
      }
    }

    const script = document.createElement('script');
    script.src = src;

    Object.keys(attributes).forEach((key) => {
      const sc = script as Any;
      if (sc[key] === undefined) {
        script.setAttribute(key, attributes[key]);
      } else {
        sc[key] = attributes[key];
      }
    });

    const handleLoad = () => {
      setLoading(false);
    };
    const handleError = (err: ErrorEvent) => {
      setError(err);
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    if (element) {
      element.appendChild(script);
    }

    return () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
  }, [attributes, checkForExisting, element, error, loading, src]);

  return [loading, error];
};
export default useScript;
